/**
 * House title-casing for names and Singapore address text.
 *
 * The rules were tuned against the real customer registry (Ben, 2026-09-09 —
 * see `scripts/lib/customerTidy.mjs`, which imports this file so the one-off
 * tidy script and the live write path can never disagree):
 *
 * - A token with a digit (`WJUL240`, `337`, `E1`) is left alone.
 * - A token already in mixed case (`McDonald`, `Weng-EE`) is human-cased and
 *   left alone.
 * - Otherwise each alphabetic run is cased separately so hyphens, slashes,
 *   apostrophes, dots and brackets survive: `ALJUNIED-HOUGANG`, `PTE.LTD.`,
 *   `(CC)`, `MONK'S`.
 * - An all-caps run that reads as initials (see `isInitials`) stays as it is,
 *   except a single letter after an apostrophe (`MONK'S` → `s`).
 * - Known acronyms stay uppercase; `s/o` and `d/o` stay lowercase.
 * - Connector words (`at`, `of`, `the` …) are lowercased after the first word.
 */

/**
 * Words kept fully uppercase even though they carry a vowel. `ID` is the trade
 * shorthand for an interior designer — a core customer segment.
 */
const ACRONYMS = new Set([
  'HDB',
  'MRT',
  'LRT',
  'PCF',
  'WSS',
  'LLP',
  'ID',
  'II',
  'III',
  'IV',
  'VI',
  'VII',
  'VIII',
]);

/** Whole tokens that are conventionally lowercase in Singapore names. */
const LOWER_TOKENS = new Set(['s/o', 'd/o']);

/** Connector words written lowercase mid-name in title case (`Treasure at Tampines`). */
const SMALL_WORDS = new Set(['at', 'of', 'the', 'and', 'in', 'on', 'by', 'for', 'to']);

/**
 * The few real vowel-less words: the surname `NG`, titles and company
 * suffixes. Every other vowel-less all-caps run is treated as initials.
 */
const VOWELLESS_WORDS = new Set(['NG', 'MR', 'MS', 'MRS', 'MDM', 'DR', 'LTD', 'MY', 'SDN', 'BHD']);

const capitalise = (seg: string): string =>
  seg.charAt(0).toUpperCase() + seg.slice(1).toLowerCase();

/**
 * An all-caps run that reads as initials rather than a word: a single letter
 * (`J S SYSTEM`) or any run with no vowel (`JM`, `RY`, `SW`, `CC`, `LJR`,
 * `YMH`). Runs WITH a vowel are ordinary words or Chinese/Malay name syllables
 * (`LI`, `OH`, `EE`, `YU`) and get capitalised.
 */
const isInitials = (seg: string): boolean =>
  seg.length === 1 || (!/[AEIOU]/i.test(seg) && !VOWELLESS_WORDS.has(seg.toUpperCase()));

const titleCaseToken = (token: string, isFirst: boolean): string => {
  if (!/[A-Za-z]/.test(token) || /\d/.test(token)) return token;
  if (LOWER_TOKENS.has(token.toLowerCase())) return token.toLowerCase();
  const hasUpper = /[A-Z]/.test(token);
  const hasLower = /[a-z]/.test(token);
  if (hasUpper && hasLower) return token;
  if (!isFirst && SMALL_WORDS.has(token.toLowerCase())) return token.toLowerCase();

  const parts = token.split(/([^A-Za-z]+)/);
  return parts
    .map((seg, i) => {
      if (!/[A-Za-z]/.test(seg)) return seg;
      const prevDelimiter = i > 0 ? parts[i - 1] : '';
      if (seg.length === 1 && prevDelimiter.endsWith("'")) return seg.toLowerCase();
      if (hasUpper && isInitials(seg)) return seg;
      if (ACRONYMS.has(seg.toUpperCase())) return seg.toUpperCase();
      return capitalise(seg);
    })
    .join('');
};

/** Title-case a string token by token; whitespace is preserved as-is. */
export const titleCase = (text: string): string => {
  let seenWord = false;
  return text
    .split(/(\s+)/)
    .map((token) => {
      if (!/\S/.test(token)) return token;
      const out = titleCaseToken(token, !seenWord);
      if (/[A-Za-z]/.test(token)) seenWord = true;
      return out;
    })
    .join('');
};

/** Trim, collapse internal whitespace, then title-case. `undefined` → ''. */
export const tidyText = (text: string | undefined | null): string =>
  titleCase((text ?? '').trim().replace(/\s+/g, ' '));
